import { apiService, API_ENDPOINTS } from '@/shared/services/api';
import type { APIResponse } from '@/shared/types';
import type { VendorIntake } from '@/features/fmo/types';

export interface LoadBalancerConfiguration {
  organizationId: string;
  vendorIntakeId: string;
  percentage: number;
}

export interface OrganizationWithLoadBalancer {
  id: string;
  name: string;
  type: 'EM' | 'DM' | 'CM' | 'BM' | 'AM';
  assignedLinks: {
    linkId: string;
    percentage: number;
    vendorIntake?: VendorIntake;
  }[];
}

export interface LoadBalancerValidationResult {
  isValid: boolean;
  message: string;
  totalPercentage: number;
}

export interface AutoConfigurationResult {
  configurations: LoadBalancerConfiguration[];
  message: string;
  totalPercentage: number;
}

export class LoadBalancerService {
  /**
   * Save load balancer configurations
   */
  static async saveConfigurations(configurations: LoadBalancerConfiguration[]): Promise<APIResponse<any>> {
    try {
      const response = await apiService.post<APIResponse<any>>(
        '/load-balancer/configurations',
        configurations
      );
      return response.data;
    } catch (error: any) {
      console.error('Error saving load balancer configurations:', error);
      throw new Error(error.response?.data?.message || 'Failed to save load balancer configurations');
    }
  }

  /**
   * Auto-configure and save load balancer configurations
   */
  static async autoConfigureAndSave(configurations: LoadBalancerConfiguration[]): Promise<APIResponse<any>> {
    try {
      const response = await apiService.post<APIResponse<any>>(
        '/load-balancer/configurations/auto-configure',
        configurations
      );
      return response.data;
    } catch (error: any) {
      console.error('Error auto-configuring load balancer configurations:', error);
      throw new Error(error.response?.data?.message || 'Failed to auto-configure load balancer configurations');
    }
  }

  /**
   * Get load balancer configurations
   */
  static async getConfigurations(): Promise<OrganizationWithLoadBalancer[]> {
    try {
      const response = await apiService.get<APIResponse<OrganizationWithLoadBalancer[]>>(
        '/load-balancer/configurations'
      );
      return response.data.data || [];
    } catch (error: any) {
      console.error('Error fetching load balancer configurations:', error);
      console.error('Error URL:', error.config?.url);
      throw new Error(error.response?.data?.message || 'Failed to fetch load balancer configurations');
    }
  }

  /**
   * Validate load balancer configurations
   */
  static async validateConfigurations(configurations: LoadBalancerConfiguration[]): Promise<LoadBalancerValidationResult> {
    try {
      const response = await apiService.post<APIResponse<LoadBalancerValidationResult>>(
        '/load-balancer/validate',
        configurations
      );
      return response.data.data || { isValid: false, message: 'Validation failed', totalPercentage: 0 };
    } catch (error: any) {
      console.error('Error validating load balancer configurations:', error);
      throw new Error(error.response?.data?.message || 'Failed to validate load balancer configurations');
    }
  }

  /**
   * Get auto-configuration preview
   */
  static async getAutoConfigurationPreview(configurations: LoadBalancerConfiguration[]): Promise<AutoConfigurationResult> {
    try {
      const response = await apiService.post<APIResponse<AutoConfigurationResult>>(
        '/load-balancer/auto-configure-preview',
        configurations
      );
      return response.data.data || { configurations: [], message: 'No configurations', totalPercentage: 0 };
    } catch (error: any) {
      console.error('Error getting auto-configuration preview:', error);
      throw new Error(error.response?.data?.message || 'Failed to get auto-configuration preview');
    }
  }

  /**
   * Get organizations for load balancer
   */
  static async getOrganizations(): Promise<any[]> {
    try {
      const response = await apiService.get<APIResponse<any[]>>(
        API_ENDPOINTS.hr.organizations
      );
      return response.data.data || [];
    } catch (error: any) {
      console.error('Error fetching organizations:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch organizations');
    }
  }

  /**
   * Get vendor intakes for load balancer
   */
  static async getVendorIntakes(): Promise<VendorIntake[]> {
    try {
      const response = await apiService.get<APIResponse<VendorIntake[]>>(
        API_ENDPOINTS.fmo.vendorIntakeLinks
      );
      return response.data.data || [];
    } catch (error: any) {
      console.error('Error fetching vendor intakes:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch vendor intakes');
    }
  }

  /**
   * Auto-configure load balancer for all organizations
   */
  static async autoConfigureAllOrganizations(): Promise<APIResponse<any>> {
    try {
      const response = await apiService.post<APIResponse<any>>(
        '/load-balancer/configurations/auto-configure-all'
      );
      return response.data;
    } catch (error: any) {
      console.error('Error auto-configuring all organizations:', error);
      throw new Error(error.response?.data?.message || 'Failed to auto-configure all organizations');
    }
  }
}
